import { create } from "zustand";
import { enqueueSnackbar } from "notistack";

export type CardViewMode = "list" | "book" | "collage";

const CARD_VIEW_MODE_KEY = "CARD_VIEW_MODE";
const LEGACY_BOOK_ICON_KEY = "BOOK_ICON_SELECTED";

const VIEW_MODE_CYCLE: CardViewMode[] = ["list", "book", "collage"];

const VIEW_MODE_MESSAGES: Record<CardViewMode, string> = {
  list: "List view enabled",
  book: "Book view mode enabled. Now all of the cards will be viewable in book view",
  collage: "Collage view enabled",
};

function isCardViewMode(value: unknown): value is CardViewMode {
  return value === "list" || value === "book" || value === "collage";
}

function getInitialViewMode(): CardViewMode {
  try {
    const storedMode = localStorage.getItem(CARD_VIEW_MODE_KEY);
    if (isCardViewMode(storedMode)) return storedMode;

    const legacy = localStorage.getItem(LEGACY_BOOK_ICON_KEY);
    if (legacy !== null) {
      return JSON.parse(legacy) === true ? "book" : "list";
    }
  } catch {
    localStorage.removeItem(CARD_VIEW_MODE_KEY);
    localStorage.removeItem(LEGACY_BOOK_ICON_KEY);
  }
  return "book";
}

function deriveViewFlags(mode: CardViewMode) {
  return {
    viewMode: mode,
    isBookIconSelected: mode === "book",
    isCollageViewSelected: mode === "collage",
    isListView: mode === "list",
  };
}

function persistViewMode(mode: CardViewMode) {
  localStorage.setItem(CARD_VIEW_MODE_KEY, mode);
  localStorage.setItem(LEGACY_BOOK_ICON_KEY, JSON.stringify(mode === "book"));
}

interface BookIconState {
  viewMode: CardViewMode;
  isBookIconSelected: boolean;
  isCollageViewSelected: boolean;
  isListView: boolean;
  setViewMode: (mode: CardViewMode) => void;
  cycleViewMode: () => void;
  setBookIconSelected: (value: boolean) => void;
  toggleBookIconSelected: () => void;
}

const initialMode = getInitialViewMode();

const useBookIconStore = create<BookIconState>((set, get) => ({
  ...deriveViewFlags(initialMode),
  setViewMode: (mode) => {
    if (get().viewMode === mode) return;
    persistViewMode(mode);
    enqueueSnackbar(VIEW_MODE_MESSAGES[mode], { variant: "info" });
    set(deriveViewFlags(mode));
  },
  cycleViewMode: () => {
    const current = get().viewMode;
    const currentIndex = VIEW_MODE_CYCLE.indexOf(current);
    const nextMode =
      VIEW_MODE_CYCLE[(currentIndex + 1) % VIEW_MODE_CYCLE.length];
    get().setViewMode(nextMode);
  },
  setBookIconSelected: (value) => {
    get().setViewMode(value ? "book" : "list");
  },
  toggleBookIconSelected: () => {
    get().cycleViewMode();
  },
}));

export default useBookIconStore;
